import { type KeyboardEvent } from 'react';

export type InventoryCategoryFilterOption = {
  category: string;
  /** Display label when different from `category` (e.g. translated text). */
  displayCategory?: string;
  emptyAtLocation: boolean;
};

type InventoryCategoryFilterRowProps = {
  options: InventoryCategoryFilterOption[];
  deselectedCategories: ReadonlySet<string>;
  onToggle: (category: string) => void;
  toggleCategoryAriaLabel: (category: string) => string;
  ariaLabel: string;
  className?: string;
};

const resolveCategoryChipClassName = (selected: boolean, emptyAtLocation: boolean): string => {
  if (emptyAtLocation) {
    if (selected) {
      return 'flex shrink-0 items-center gap-2 rounded-xl border-2 border-amber-500 bg-amber-400 px-4 py-2 text-xs font-bold text-amber-950 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500/45 dark:border-amber-400 dark:bg-amber-500 dark:text-amber-950';
    }
    return 'flex shrink-0 items-center gap-2 rounded-xl border-2 border-amber-200 bg-amber-50 px-4 py-2 text-xs font-bold text-amber-900 transition-colors hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-400/35 dark:border-amber-700 dark:bg-amber-950/45 dark:text-amber-100 dark:hover:bg-amber-950/70';
  }
  if (selected) {
    return 'flex shrink-0 items-center gap-2 rounded-xl border-2 border-primary-600 bg-primary-600 px-4 py-2 text-xs font-bold text-white shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/45';
  }
  return 'flex shrink-0 items-center gap-2 rounded-xl border-2 border-gray-200 bg-white px-4 py-2 text-xs font-bold text-gray-500 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/25 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-400';
};

export const InventoryCategoryFilterRow = ({
  options,
  deselectedCategories,
  onToggle,
  toggleCategoryAriaLabel,
  ariaLabel,
  className = '',
}: InventoryCategoryFilterRowProps) => {
  const handleChipKeyDown = (event: KeyboardEvent<HTMLButtonElement>, category: string) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    onToggle(category);
  };

  const wrapperClassName = ['flex flex-nowrap gap-2 overflow-x-auto pt-0.5 pb-2', className].filter(Boolean).join(' ');

  const resolveChipEdgeClassName = (index: number) => {
    const isFirst = index === 0;
    const isLast = index === options.length - 1;
    return [isFirst ? 'ml-1' : '', isLast ? 'mr-1' : ''].filter(Boolean).join(' ');
  };

  return (
    <div className={wrapperClassName} role="group" aria-label={ariaLabel}>
      {options.map(({ category, displayCategory, emptyAtLocation }, index) => {
        const selected = !deselectedCategories.has(category);
        const dotClassName = emptyAtLocation ? 'bg-amber-500 dark:bg-amber-400' : 'bg-gray-400';
        const edgeClassName = resolveChipEdgeClassName(index);
        const label = (displayCategory ?? category).trim() !== '' ? (displayCategory ?? category) : category;
        return (
          <button
            key={category}
            type="button"
            tabIndex={0}
            aria-pressed={selected}
            aria-label={toggleCategoryAriaLabel(label)}
            onClick={() => onToggle(category)}
            onKeyDown={(event) => handleChipKeyDown(event, category)}
            className={`${resolveCategoryChipClassName(selected, emptyAtLocation)} ${edgeClassName}`}
          >
            {selected ? (
              <svg className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotClassName}`} aria-hidden />
            )}
            {label}
          </button>
        );
      })}
    </div>
  );
};
